import { IntlProvider } from 'react-intl';
import messages_en from '@/locales/en.json';
import messages_es from '@/locales/es.json';
import messages_bg from '@/locales/bg.json';
import { createContext, useEffect, useState } from 'react';


// English is the source catalogue; every other locale is layered on top of it,
// so a key that has not been translated yet renders its English string instead
// of the raw message id. What react-intl still reports as a missing translation
// is then an id absent from en.json itself: a real bug, so its default error
// handler stays on. Built once here, not per render: IntlProvider rebuilds its
// formatters whenever the messages object changes identity.
const messages = {
    en: messages_en,
    es: { ...messages_en, ...messages_es },
    bg: { ...messages_en, ...messages_bg },
}

export const LanguageContext = createContext();
const supportedLanguages = ["en", "es", "bg"];

// The browser's own language: the base of navigator.language when the site
// speaks it, else English. What a visitor who never chose one reads.
const browserLanguage = () => {
    const base = navigator.language.split(/[-_]/)[0];
    return supportedLanguages.includes(base) ? base : 'en';
};

/**
 * The cookie that tells the server (App\Support\Marketing\SiteLocale) the
 * locale a visitor chose, so the head it renders speaks the page's language.
 * Plain text (the server excepts it from cookie encryption), site-wide, and
 * kept for a year, like the localStorage choice it mirrors.
 *
 * The provider writes it only while the chosen locale differs from the
 * browser's own and expires it once they agree, so a visitor who never used
 * the language switch gets no cookie at all: the server then reads that same
 * browser language from the first Accept-Language entry. A preference cookie
 * set only on the visitor's own choice needs no consent banner, and the site
 * has none.
 */
export function localeCookie(locale, secure = window.location.protocol === 'https:') {
    return `locale=${encodeURIComponent(locale)}; path=/; max-age=31536000; samesite=lax${secure ? '; secure' : ''}`;
}

const expiredLocaleCookie = 'locale=; path=/; max-age=0; samesite=lax';

export const LanguageProvider = ({ children }) => {
    const defaultLang = () => localStorage.getItem('locale') || browserLanguage();
    const [locale, setLocale] = useState(defaultLang);
    useEffect(() => {
        localStorage.setItem('locale', locale);
        document.cookie = locale === browserLanguage() ? expiredLocaleCookie : localeCookie(locale);
    }, [locale]);
    return (
        <LanguageContext.Provider value={{ locale, setLocale }}>
            <IntlProvider locale={locale} messages={messages[locale] ?? messages.en}>
                {children}
            </IntlProvider>
        </LanguageContext.Provider>
    );
}
